import { get } from 'lodash-es';
import createCachedSelector from 're-reselect';

import { REFUND_ASSIST_VIEW_ID, VOIDED_VIEW_ID } from '../../../components/shipping-grid/views/view-id-constants';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { GridConfigService } from '../../../services/grid-config-service';
import {
  baseColumnsDefaultConfigs,
  defaultColumnConfigurations,
  refundAssistColumnsDefaultConfigs,
  voidedColumnsDefaultConfigs,
} from '../../../store/shipments/grid/default-column-configuration';
import type { AmazonShipmentUpdate, ShipmentsGridColumnConfig, ShipmentsGridState, ShipmentsGridViewProps } from './shipments.grid.types';
import type { RootState } from 'store/root-state';

const EMAIL_COLUMN_IDS = ['delivery-notification', 'shipment-email-notified'];

export const selectIsLoading = (state: ShipmentsGridState): boolean => get(state, 'isLoading');
export const selectPage = (state: ShipmentsGridState): number => get(state, 'page');
export const selectPageSize = (state: ShipmentsGridState): number => get(state, 'pageSize');
export const selectTotalRecords = (state: ShipmentsGridState): number | undefined => get(state, 'totalRecords');
export const selectTotalPages = (state: ShipmentsGridState): number => get(state, 'totalPages');
export const selectViewStartingCount = (state: ShipmentsGridState): number => get(state, 'viewStartingCount');
export const selectViewEndingCount = (state: ShipmentsGridState): number => get(state, 'viewEndingCount');
export const selectUserGridConfigurations = (state: ShipmentsGridState): ShipmentsGridState['userViewConfigurations'] =>
  get(state, 'userViewConfigurations', emptyObject);
export const selectSearchTerm = (state: ShipmentsGridState): string | undefined => get(state, 'searchTerm');
export const selectCurrentPageFulfillmentIds = (state: ShipmentsGridState): string[] =>
  get(state, 'currentPageFulfillmentIds', emptyArray as string[]);

const mergeConfigurationsSelector: (
  viewConfiguration: ShipmentsGridColumnConfig[] | undefined,
  statusViewId: string,
  activeFilterViewId: string,
  enableNewEmailExperience: boolean,
) => ShipmentsGridColumnConfig[] = createCachedSelector(
  (viewConfiguration: ShipmentsGridColumnConfig[] | undefined) => viewConfiguration,
  (
    _viewConfiguration: ShipmentsGridColumnConfig[] | undefined,
    _statusViewId: string,
    _activeFilterViewId: string,
    enableNewEmailExperience: boolean,
  ) => enableNewEmailExperience,
  (viewConfiguration, enableNewEmailExperience): ShipmentsGridColumnConfig[] => {
    let config: ShipmentsGridColumnConfig[] = GridConfigService.mergeUserAndDefaultConfigs(viewConfiguration, defaultColumnConfigurations);
    if (enableNewEmailExperience) {
      const filtered = config.filter((col) => !EMAIL_COLUMN_IDS.includes(col.id));
      config = filtered.length === 0 ? (emptyArray as ShipmentsGridColumnConfig[]) : filtered;
    }
    return GridConfigService.mergeUserAndDefaultConfigs(config, defaultColumnConfigurations);
  },
)(
  (
    _viewConfiguration: ShipmentsGridColumnConfig[] | undefined,
    statusViewId: string,
    activeFilterViewId: string,
    enableNewEmailExperience: boolean,
  ) => `${statusViewId}_${activeFilterViewId}_${enableNewEmailExperience}`,
);

const selectCustomViewColumnConfiguration = (state: ShipmentsGridState): ShipmentsGridColumnConfig[] | undefined =>
  get(state, 'customViewColumnConfiguration');

export const selectCurrentViewGridConfigOrDefault = (
  state: ShipmentsGridState,
  { activeFilterViewId, statusViewId, enableNewEmailExperience = false }: ShipmentsGridViewProps,
): ShipmentsGridColumnConfig[] => {
  if (activeFilterViewId === 'custom') {
    const viewConfiguration = selectCustomViewColumnConfiguration(state);
    return mergeConfigurationsSelector(viewConfiguration, statusViewId, activeFilterViewId, enableNewEmailExperience);
  }

  const allGridConfigs = selectUserGridConfigurations(state);
  const { [activeFilterViewId]: configForView } = allGridConfigs[statusViewId] || {};

  if (!configForView) {
    if (statusViewId === REFUND_ASSIST_VIEW_ID) return refundAssistColumnsDefaultConfigs;
    if (statusViewId === VOIDED_VIEW_ID) return voidedColumnsDefaultConfigs;
    return baseColumnsDefaultConfigs;
  }

  return mergeConfigurationsSelector(configForView, statusViewId, activeFilterViewId, enableNewEmailExperience);
};

export const selectCurrentOrderByColumn = (
  state: ShipmentsGridState,
  { statusViewId, activeFilterViewId }: ShipmentsGridViewProps,
): ShipmentsGridColumnConfig | undefined => {
  const gridConfig =
    selectCurrentViewGridConfigOrDefault(state, {
      statusViewId,
      activeFilterViewId,
    }) || emptyArray;

  return gridConfig.find((col: ShipmentsGridColumnConfig) => !!col.sorted);
};

export const selectAmazonStatusUpdateByFulfillmentId = (
  state: ShipmentsGridState,
  { fulfillmentId }: { fulfillmentId: string },
): AmazonShipmentUpdate | undefined => state.amazonShipmentUpdates[fulfillmentId];

export const selectShipmentsGridAmazonStatusUpdateByFulfillmentId = (
  state: RootState,
  props: { fulfillmentId: string },
): AmazonShipmentUpdate | undefined => selectAmazonStatusUpdateByFulfillmentId(get(state, 'shipments.grid'), props);
export const selectShipmentsGridCurrentOrderByColumn = (
  state: RootState,
  props: ShipmentsGridViewProps,
): ShipmentsGridColumnConfig | undefined => selectCurrentOrderByColumn(get(state, 'shipments.grid'), props);
export const selectShipmentsGridCurrentPageFulfillmentIds = (state: RootState): string[] =>
  selectCurrentPageFulfillmentIds(get(state, 'shipments.grid'));
export const selectShipmentsGridCurrentViewGridConfigOrDefault = (
  state: RootState,
  props: ShipmentsGridViewProps,
): ShipmentsGridColumnConfig[] => selectCurrentViewGridConfigOrDefault(get(state, 'shipments.grid'), props);
export const selectShipmentsGridIsLoading = (state: RootState): boolean => selectIsLoading(get(state, 'shipments.grid'));
export const selectShipmentsGridPage = (state: RootState): number => selectPage(get(state, 'shipments.grid'));
export const selectShipmentsGridPageSize = (state: RootState): number => selectPageSize(get(state, 'shipments.grid'));
export const selectShipmentsGridSearchTerm = (state: RootState): string | undefined => selectSearchTerm(get(state, 'shipments.grid'));
export const selectShipmentsGridTotalPages = (state: RootState): number => selectTotalPages(get(state, 'shipments.grid'));
export const selectShipmentsGridTotalRecords = (state: RootState): number | undefined => selectTotalRecords(get(state, 'shipments.grid'));
export const selectShipmentsGridUserGridConfigurations = (state: RootState): ShipmentsGridState['userViewConfigurations'] =>
  selectUserGridConfigurations(get(state, 'shipments.grid'));
export const selectShipmentsGridViewEndingCount = (state: RootState): number => selectViewEndingCount(get(state, 'shipments.grid'));
export const selectShipmentsGridViewStartingCount = (state: RootState): number => selectViewStartingCount(get(state, 'shipments.grid'));
